import React, { useEffect, useState } from 'react';
import { Paper, Stack, Typography } from '@mui/material';
import { GridColDef, GridRowParams, GridSortModel } from '@mui/x-data-grid-pro';
import { LessonDto, LessonStateDto } from '../../../../api-service/clients';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { DataGrid } from '../../../search/components/data-grid/data-grid';
import { formatDateTime, toStartOfDay } from '../../../../utilities';
import { attendanceAreaPathName, getLessonEditPath, getLessonProtocolPath } from '../../attendance-area-pathnames';
import { getLessonStateDisplayValue, toSchultzDate } from '../../utils';
import { useAttendanceRights } from '../../use-attendance-rights';

const attendanceClient = moduleClientFactory.createAttendanceClient();
const attendanceProtocolClient = moduleClientFactory.createAttendanceProtocolClient();

const columns: GridColDef[] = [
  {
    field: 'beginAt',
    headerName: 'Start',
    flex: 1,
    renderCell: ({ value }) => formatDateTime(value),
  },
  {
    field: 'endAt',
    headerName: 'Slut',
    flex: 1,
    renderCell: ({ value }) => formatDateTime(value),
  },
  {
    field: 'occupiedSeats',
    headerName: 'Antal deltagere',
    flex: 1,
  },
  {
    field: 'availableSeats',
    headerName: 'Ledige pladser',
    flex: 1,
    valueGetter: ({ row }) => getAvailableSeats(row as LessonDto),
  },
  {
    field: 'educatorNames',
    headerName: 'Undervisere',
    flex: 1,
    valueGetter: ({ value }) => ((value as string[] | undefined) ?? []).join(', '),
  },
  {
    field: 'lessonState',
    headerName: 'Status',
    flex: 1,
    valueGetter: ({ value }) => getLessonStateDisplayValue(value),
  },
];

function getAvailableSeats(lesson: LessonDto) {
  return lesson.capacity == null ? undefined : lesson.capacity - lesson.occupiedSeats;
}

function isCurrentOrUpcoming(lesson: LessonDto) {
  return lesson.lessonState !== LessonStateDto.Cancelled && toSchultzDate(lesson.beginAt) >= toStartOfDay(new Date());
}

interface ICourseLessonsPanelProps {
  courseId: string;
}

export function CourseLessonsPanel(props: ICourseLessonsPanelProps) {
  const { courseId } = props;
  const { hasOnlyProtocolAccess } = useAttendanceRights();
  const [lessons, setLessons] = useState<LessonDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [sorting, setSorting] = useState<GridSortModel>([{ field: 'beginAt', sort: 'asc' }]);

  useEffect(() => {
    async function loadLessons() {
      try {
        const result = hasOnlyProtocolAccess
          ? await attendanceProtocolClient.getLessonsForAttendanceProtocol({ courseId })
          : await attendanceClient.getLessonsByCourseId({ courseId });
        setLessons((result.lessons || []).filter(isCurrentOrUpcoming));
      } catch (error) {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    }
    loadLessons();
  }, [courseId]);

  function handleRowClick({ row }: GridRowParams) {
    const { lessonId } = row as LessonDto;
    window.open(`/${attendanceAreaPathName}/${hasOnlyProtocolAccess ? getLessonProtocolPath(lessonId) : getLessonEditPath(lessonId)}`);
  }

  return (
    <Stack sx={{ py: 0, boxSizing: 'border-box' }} direction="column">
      <Paper sx={{ flex: 1, ml: 7, width: '95%', mr: 0 }}>
        <Typography variant="body1" sx={{ padding: 1, backgroundColor: '#F4F4F4', fontWeight: 600, color: '#333' }}>
          Aktuelle og planlagte lektioner
        </Typography>
        {hasError ? (
          <Typography variant="body2" sx={{ padding: 1 }}>
            Der skete en fejl ved hentning af lektioner
          </Typography>
        ) : (
          <DataGrid
            mode="client"
            rows={lessons}
            columns={columns}
            getRowId={row => row.lessonId}
            isLoading={isLoading}
            pageSize={10}
            sorting={sorting}
            onSort={setSorting}
            onRowClick={handleRowClick}
            disableColumnMenu
          />
        )}
      </Paper>
    </Stack>
  );
}
